import { match } from 'path-to-regexp';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { isAuthenticatedSelector } from '../ducks/auth.duck';
import routeConfiguration from '../routing/routeConfiguration';
import enMessages from '../translations/en.json';
import { SUPPORTED_LANGUAGES } from '../util/constants';
import { detectUserDefaultLanguage } from '../util/data';
import { TRANSLATED_PATHS, createSlug, getTranslatedPath } from '../util/urlHelpers';

// Lazy-load language messages
const languageMap = {
  en: () => import('../translations/en.json'),
  fr: () => import('../translations/fr.json'),
  es: () => import('../translations/es.json'),
  zh: () => import('../translations/zh.json'),
};

export const LanguageContext = createContext({ locale: 'en', messages: enMessages, changeLanguage: () => { } });

const LanguageProviderComponent = ({ children, history }) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const defaultLocale = detectUserDefaultLanguage(); // browser or saved preference
  const locationPath = typeof window !== 'undefined' ? window.location.pathname : '';

  // Extract locale from URL (e.g., /fr/page), default to browser preference
  const fromUrl = SUPPORTED_LANGUAGES.find(
    code => code !== 'en' && (locationPath.startsWith(`/${code}/`) || locationPath === `/${code}`)
  ) || "en";

  const [locale, setLocale] = useState(fromUrl || defaultLocale);

  // Always start with English; the useEffect below loads the correct locale asynchronously.
  // The synchronous require() calls that were here forced fr/es/zh (203 KB gzip) into the main bundle.
  const [messages, setMessages] = useState(enMessages);

  // Remove "/en" prefix from URL if present
  useEffect(() => {
    if (locationPath.startsWith('/en')) {
      const stripped = locationPath.replace(/^\/en/, '') || '/';
      history.replace(stripped);
    }
  }, [locationPath, history]);

  // Load messages and update locale when URL changes
  useEffect(() => {
    const selectedLocale = fromUrl || locale;
    if (languageMap[selectedLocale]) {
      languageMap[selectedLocale]()
        .then(module => {
          // Merge English first so any key missing from the active locale's
          // bundle falls back to the English string instead of react-intl
          // rendering the raw message id (matches the SSR path's fallback
          // behavior, which always renders defaultMessages/en.json).
          setMessages({ ...enMessages, ...module.default });

          const storedLanguage = typeof window !== 'undefined' && window.localStorage.getItem('appLanguage');
          if (fromUrl && (fromUrl !== locale || fromUrl !== storedLanguage)) {
            typeof window !== 'undefined' && window.localStorage.setItem('appLanguage', fromUrl);
            setLocale(fromUrl);

            if (isAuthenticated) {
              dispatch(updateProfile({ publicData: { language: fromUrl } })).catch(() => {
                // Error is already handled via Redux state
                // This catch prevents unhandled promise rejection
              });
            }
          }
        });
    }
  }, [fromUrl, locale, isAuthenticated, dispatch]);

  // Change language and navigate to translated route
  const changeLanguage = useCallback(({ locale: newLocale, listing, pageAssetsData, pageId }) => {
    if (newLocale === locale) return;

    // const translatedPageAssetName = pageAssetsData?.meta?.[newLocale === 'en' ? 'pageId' : `page_${newLocale}`];
    const translatedPageAssetName = pageAssetsData?.meta?.['pageId'];

    typeof window !== 'undefined' && window.localStorage.setItem('appLanguage', newLocale);
    setLocale(newLocale);

    if (isAuthenticated) {
      dispatch(updateProfile({ publicData: { language: newLocale } })).catch(() => {
        // Error is already handled via Redux state
        // This catch prevents unhandled promise rejection
      });
    }

    const routes = routeConfiguration();
    const currentPath = window.location.pathname;

    const pathParts = currentPath.split('/').filter(Boolean);
    const maybeLang = pathParts[0];
    const currentLocale = SUPPORTED_LANGUAGES.includes(maybeLang) ? maybeLang : 'en';

    const strippedPath = SUPPORTED_LANGUAGES.includes(maybeLang)
      ? `/${pathParts.slice(1).join('/')}`
      : currentPath;

    let matchedRoute = null;
    let params = {};

    // Match current route to find route definition
    for (const route of routes) {
      const fallbackPath = TRANSLATED_PATHS?.[route.name]?.['en']; // Always fallback to English
      const translatedPath = getTranslatedPath(route.name, fallbackPath, currentLocale);
      const matcher = match(translatedPath, { decode: decodeURIComponent });
      const result = matcher(decodeURIComponent(strippedPath));

      if (result) {
        matchedRoute = route;
        params = result.params;
        break;
      }
    }

    let translatedPath = '/';

    if (matchedRoute) {
      let translated = getTranslatedPath(matchedRoute.name, matchedRoute.path, newLocale);

      // Replace route params (e.g., :slug, :id, :pageId)
      Object.entries(params).forEach(([key, val]) => {
        if (key === 'slug' && listing?.attributes) {
          const englishTitle = listing.attributes.title || '';
          const localizedTitle = listing?.attributes?.publicData?.[newLocale]?.title;
          const localizedSlug = localizedTitle || englishTitle;
          const slugResult = createSlug(localizedSlug);
          // Non-ASCII scripts (e.g. Chinese) produce percent-encoded slugs via encodeURIComponent.
          // Fall back to the English slug so the switched-to URL remains human-readable ASCII.
          const finalSlug = (slugResult === 'no-slug' || slugResult.includes('%'))
            ? createSlug(englishTitle)
            : slugResult;
          translated = translated.replace(`:${key}`, finalSlug !== 'no-slug' ? finalSlug : val);
        } else if (key === 'pageId' && translatedPageAssetName) {
          translated = translated.replace(`:${key}`, translatedPageAssetName);
        } else {
          translated = translated.replace(`:${key}`, val);
        }
      });

      translatedPath = translated;
    }

    let searchParams = typeof window !== 'undefined' ? window.location.search : '';
    // When switching to English on store profile, add locale=en so the server does not redirect back to store default
    if (newLocale === 'en' && matchedRoute?.name === 'ProfilePage') {
      const params = new URLSearchParams(searchParams.replace(/^\?/, ''));
      params.set('locale', 'en');
      searchParams = `?${params.toString()}`;
    }
    const finalPath =
      newLocale === 'en'
        ? `${translatedPath}${searchParams}`
        : `/${newLocale}${translatedPath}${searchParams}`;

    if (typeof window !== 'undefined' && (window.location.pathname + window.location.search !== finalPath)) {
      if (matchedRoute?.name === 'CheckoutPage') {
        history.replace(finalPath); // Avoid full reload for checkout
      } else {
        // window.location.href = finalPath; // Full reload for other pages
        window.location.replace(finalPath);
      }
    }
  }, [locale, isAuthenticated, dispatch]);


  const contextValue = useMemo(() => ({
    locale,
    messages,
    changeLanguage,
  }), [locale, messages, changeLanguage]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const LanguageProvider = withRouter(LanguageProviderComponent);
